import { PULSE_COLORS } from './pulseConfig.js'
import { colorFromName, getInitials } from './pulseHelpers.js'

const PALETTE = ['#F59E0B', '#3B82F6', '#10B981', '#EC4899', '#8B5CF6', '#EF4444']

// Badge tinted backgrounds — kept warm against the dark card.
const BADGE_COLORS = {
    'QA Guru': { bg: '#4c1d95', text: '#C4B5FD', border: '#6d28d9' },
    'Bug Champion': { bg: '#78350f', text: '#FDE68A', border: '#92400e' },
    'Bug Squasher': { bg: '#064e3b', text: '#A7F3D0', border: '#065f46' },
    'Automation Cyborg': { bg: '#1e3a8a', text: '#BFDBFE', border: '#1d4ed8' },
    'Automation Champ': { bg: '#3730a3', text: '#C7D2FE', border: '#4338ca' },
    'Tooling Wizard': { bg: '#831843', text: '#FBCFE8', border: '#9d174d' },
    Closer: { bg: '#881337', text: '#FECDD3', border: '#9f1239' },
    'MR Beast': { bg: '#78350f', text: '#FDE68A', border: '#92400e' },
}

function badgeStyle(badge) {
    return BADGE_COLORS[badge] ?? { bg: PULSE_COLORS.border, text: PULSE_COLORS.text, border: PULSE_COLORS.textMuted }
}

export default function BadgeChampions({ members, limit = 6, headerAction }) {
    const ranked = members
        .filter((m) => m.qaTeam && m.badges.length > 0)
        .sort((a, b) => b.badges.length - a.badges.length || b.score - a.score)
        .slice(0, limit)

    return (
        <div
            style={{
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${PULSE_COLORS.border}`,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '16px',
                }}
            >
                <h3
                    style={{
                        color: PULSE_COLORS.text,
                        fontWeight: 600,
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    🏆 Badge Champions
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', color: PULSE_COLORS.textMuted }}>
                        Ranked by badges earned this period
                    </span>
                    {headerAction}
                </div>
            </div>

            {ranked.length === 0 ? (
                <p style={{ fontSize: '13px', color: PULSE_COLORS.textMuted }}>
                    No champions crowned for this window yet.
                </p>
            ) : (
                <ul
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                    }}
                >
                    {ranked.map((m, idx) => {
                        const color = colorFromName(m.name, PALETTE)
                        return (
                            <li
                                key={m.name}
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '12px',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    background: PULSE_COLORS.bgInner,
                                    border: `1px solid ${PULSE_COLORS.border}`,
                                }}
                            >
                                <span
                                    style={{
                                        width: '24px',
                                        textAlign: 'center',
                                        fontSize: '13px',
                                        fontWeight: 700,
                                        color: PULSE_COLORS.textSubtle,
                                        marginTop: '4px',
                                    }}
                                >
                                    {idx + 1}
                                </span>
                                <span
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#fff',
                                        fontSize: '13px',
                                        fontWeight: 700,
                                        flexShrink: 0,
                                        backgroundColor: color,
                                    }}
                                >
                                    {getInitials(m.name)}
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '8px',
                                            marginBottom: '4px',
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                color: PULSE_COLORS.text,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {m.name}
                                        </span>
                                        <span
                                            style={{ fontSize: '11px', fontWeight: 600, color: PULSE_COLORS.textMuted }}
                                        >
                                            {m.badges.length} badge{m.badges.length === 1 ? '' : 's'}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {m.badges.map((b) => {
                                            const s = badgeStyle(b.badge)
                                            return (
                                                <span
                                                    key={b.badge}
                                                    title={`${b.description} (${b.metric} ${b.metricLabel})`}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        padding: '2px 8px',
                                                        borderRadius: '999px',
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        backgroundColor: s.bg,
                                                        color: s.text,
                                                        border: `1px solid ${s.border}`,
                                                    }}
                                                >
                                                    <span>{b.emoji}</span>
                                                    <span>{b.badge}</span>
                                                    <span style={{ opacity: 0.7 }}>· {b.metric}</span>
                                                </span>
                                            )
                                        })}
                                    </div>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            )}
        </div>
    )
}
